/**
 * @file SafeArea.ts
 * @role Mobile safe-area insets (notches, home indicators) as plain numbers, plus a
 *       browser reader. Injected into LayoutService so it stays unit-testable.
 * @owns SafeArea type, ZERO_SAFE_AREA, readSafeAreaInsets().
 * @neverDoes Position UI. Consumers read insets via LayoutContext.
 * @invariants 14.
 * @see docs/UI_LAYOUT_GUIDE.md
 */
export interface SafeArea {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export const ZERO_SAFE_AREA: SafeArea = { top: 0, right: 0, bottom: 0, left: 0 };

/**
 * Reads persistent CSS custom properties populated from `env(safe-area-inset-*)` on the game
 * host. Returns zeros when there is no DOM or the browser does not expose insets.
 */
export function readSafeAreaInsets(host?: Element): SafeArea {
  if (typeof document === 'undefined' || typeof getComputedStyle === 'undefined') {
    return { ...ZERO_SAFE_AREA };
  }
  const target = host ?? document.getElementById('game-root') ?? document.documentElement;
  const style = getComputedStyle(target);
  const insets: SafeArea = {
    top: parseFloat(style.getPropertyValue('--safe-area-top')) || 0,
    right: parseFloat(style.getPropertyValue('--safe-area-right')) || 0,
    bottom: parseFloat(style.getPropertyValue('--safe-area-bottom')) || 0,
    left: parseFloat(style.getPropertyValue('--safe-area-left')) || 0,
  };
  return insets;
}
